import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Input, Button, Checkbox, SearchableSelect } from '../atoms';
import { Modal } from './Modal';
import { RichTextField } from './RichTextField';
import { useToast } from './Toast';
import { LEVEL_LABEL, type RecipientLevel } from '../lib/recipientLevel';

/**
 * NewInformationRequestModal — the create-per-property information-request flow
 * (7/26 C). Two steps:
 *   1. Compose — pick a client, one/all of its ACTIVE properties, and the document
 *      types to request (from the single master document-types list), plus an
 *      optional title / tax year / due date.
 *   2. Preview — the exact email + recipient list a send would produce
 *      (POST /api/information-requests/preview), then either create the drafts or
 *      create + send them.
 *
 * D4 — each selected property gets its OWN request (POST /api/information-requests/
 * bulk creates one draft per property). "Send now" then sends each via the existing
 * POST /:id/send (which emails all the client's saved contacts). The recipients the
 * firm approves in the preview are exactly who is emailed (shared server resolver).
 *
 * `defaultClientId` pre-selects + LOCKS the client (used by the client-profile /
 * property-setup wiring). Pure props in; the caller invalidates its own list via
 * `onCreated`.
 */

interface NewInformationRequestModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-select + lock the client (from a client profile / property-setup card). */
  defaultClientId?: string;
  /** Pre-select specific properties (e.g. a promoted property-setup card's result). */
  defaultPropertyIds?: string[];
  /** When launched from a property-setup card: if exactly one request is created it
   *  links back to that setup request (D2 bridge → property_setup_requests). */
  setupRequestId?: string;
  /** Called after a successful create/send (before onClose) so the caller can invalidate. */
  onCreated?: () => void;
}

interface NamedRow { id: string; name: string | null; }
interface PropertyRow { id: string; name: string | null; status: string | null; }
interface PreviewRecipient { name: string; email: string; role: string | null; level?: RecipientLevel; }
interface PreviewResponse {
  subject: string;
  body_text: string;
  /** 207 (ATA part 3 §6): the firm's template rendered for this client, editable; the attachments the send carries. */
  body_html?: string;
  template_body_html?: string | null;
  attachments?: Array<{ filename: string; bytes: number }>;
  recipients: PreviewRecipient[];
  recipients_total: number;
}
interface BulkResponse { created: Array<{ id: string; property_id: string; title: string }>; count: number; }

export function NewInformationRequestModal({ open, onClose, defaultClientId, defaultPropertyIds, setupRequestId, onCreated }: NewInformationRequestModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [step, setStep] = useState<'compose' | 'preview'>('compose');
  // 207 (ATA part 3 §6): the operator's edit of the rendered template, and the attachments to send.
  const [bodyHtml, setBodyHtml] = useState('');
  const [attachList, setAttachList] = useState(true);
  const [attachSummary, setAttachSummary] = useState(false);
  const [clientId, setClientId] = useState<string | null>(defaultClientId ?? null);
  // 7/28 C3: property selection is a single source of truth (propIds). Default is NOTHING
  // selected — the user either clicks "All active properties" (selects all into the list) or
  // picks individually from the always-visible, scrollable list.
  const [propIds, setPropIds] = useState<Set<string>>(new Set(defaultPropertyIds ?? []));
  const [types, setTypes] = useState<Set<string>>(new Set());
  const [title, setTitle] = useState('');
  const [taxYear, setTaxYear] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  // 7/28 C2 — which candidate emails will actually be sent to. Seeded to ALL on preview
  // (the prior behaviour: everyone), then the user can deselect down to a subset.
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());

  // The modal is a PERSISTENT mount (open just toggles), so the useState initializers
  // above only ran once — at first mount, when the caller's props were still null.
  // Re-seed the form from the CURRENT props every time it opens, so a per-card launch
  // actually pre-selects its client/property and no stale selection leaks between opens.
  useEffect(() => {
    if (!open) return;
    setStep('compose');
    setClientId(defaultClientId ?? null);
    setPropIds(new Set(defaultPropertyIds ?? []));
    setTypes(new Set());
    setTitle(''); setTaxYear(''); setDueDate(''); setPreview(null); setSelectedEmails(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const clientLocked = !!defaultClientId;

  // Data — clients (unless locked), the client's active properties, the master doc types.
  const clientsQuery = useQuery<NamedRow[]>({
    queryKey: ['clients-for-info-request'],
    queryFn: ({ signal }) => api.get<NamedRow[]>('/api/clients', signal),
    enabled: open && !clientLocked,
  });
  // Locked client (launched from a client / property-setup context): fetch just its
  // name so the field + preview summary show the client by NAME, not the raw id.
  const lockedClientQuery = useQuery<NamedRow>({
    queryKey: ['client-for-info-request', defaultClientId],
    queryFn: ({ signal }) => api.get<NamedRow>(`/api/clients/${defaultClientId}`, signal),
    enabled: open && clientLocked,
  });
  const propertiesQuery = useQuery<PropertyRow[]>({
    queryKey: ['properties-for-info-request', clientId],
    queryFn: ({ signal }) => api.get<PropertyRow[]>(`/api/properties?client_id=${clientId}`, signal),
    enabled: open && !!clientId,
  });
  const docTypesQuery = useQuery<string[] | { values?: string[] }>({
    queryKey: ['org-settings', 'document-types'],
    queryFn: ({ signal }) => api.get('/api/org-settings/document-types', signal),
    enabled: open,
  });

  const activeProps = (propertiesQuery.data ?? []).filter(p => (p.status ?? 'active') === 'active');
  const docTypes = normalizeValues(docTypesQuery.data);
  const clientName = clientLocked
    ? (lockedClientQuery.data?.name ?? undefined)
    : (clientsQuery.data ?? []).find(c => c.id === clientId)?.name ?? undefined;

  function reset() {
    setStep('compose');
    setClientId(defaultClientId ?? null);
    setPropIds(new Set(defaultPropertyIds ?? []));
    setTypes(new Set());
    setTitle(''); setTaxYear(''); setDueDate('');
    setPreview(null);
    setBodyHtml(''); setAttachList(true); setAttachSummary(false);
  }
  function close() { reset(); onClose(); }

  // Which properties this send will target (for the request body + the summary).
  const targetPropertyIds = [...propIds];
  const allActiveSelected = activeProps.length > 0 && activeProps.every(p => propIds.has(p.id));
  const chosenTypes = [...types];
  const composeValid = !!clientId && targetPropertyIds.length > 0 && chosenTypes.length > 0;

  function requestBody() {
    return {
      client_id: clientId,
      property_ids: [...propIds],
      document_types: chosenTypes,
      ...(title.trim() ? { title: title.trim() } : {}),
      ...(taxYear ? { tax_year: Number(taxYear) } : {}),
      ...(dueDate ? { due_date: dueDate } : {}),
      ...(setupRequestId ? { link_setup_request_id: setupRequestId } : {}),
      // 207 (§6): the edited body (the template as reviewed) and the attachment choices, per request.
      ...(bodyHtml.trim() ? { body_html: bodyHtml } : {}),
      attach_property_list: attachList,
      attach_summary_report: attachSummary,
    };
  }

  const previewMut = useMutation<PreviewResponse, Error, void>({
    mutationFn: () => api.post<PreviewResponse>('/api/information-requests/preview', {
      client_id: clientId, title: title.trim() || undefined, document_types: chosenTypes,
      property_ids: [...propIds],
      // 207 (§6): the firm's template wording, editable in the preview step.
      use_template: true,
      ...(taxYear ? { tax_year: Number(taxYear) } : {}),
      attach_property_list: attachList,
      attach_summary_report: attachSummary,
    }),
    onSuccess: (data) => {
      setPreview(data);
      setBodyHtml(data.body_html ?? '');
      setSelectedEmails(new Set(data.recipients.map((r) => r.email)));
      setStep('preview');
    },
    onError: (err) => toast({ variant: 'error', message: apiError(err, 'Could not build the preview.') }),
  });

  // Create the drafts (one per property); optionally send each right away. Send is
  // BEST-EFFORT: a per-request send failure must NOT reject the whole mutation (the
  // drafts already exist), else the modal would stay open and a retry would re-run
  // /bulk — duplicating requests + re-emailing. Failed sends are reported; those
  // drafts remain sendable individually from the board.
  const createMut = useMutation<{ count: number; sent: number; sendFailed: number; requested: boolean }, Error, boolean>({
    mutationFn: async (send: boolean) => {
      const res = await api.post<BulkResponse>('/api/information-requests/bulk', requestBody());
      let sent = 0, sendFailed = 0;
      if (send) {
        // 7/28 C2 — email only the recipients the user kept selected (validated
        // server-side against each request's own candidates).
        const recipient_emails = [...selectedEmails];
        for (const r of res.created) {
          try { await api.post(`/api/information-requests/${r.id}/send`, { recipient_emails }); sent++; }
          catch { sendFailed++; }
        }
      }
      return { count: res.count, sent, sendFailed, requested: send };
    },
    onSuccess: ({ count, sent, sendFailed, requested }) => {
      queryClient.invalidateQueries({ queryKey: ['information-requests-board'] });
      queryClient.invalidateQueries({ queryKey: ['information-requests'] });
      const noun = (n: number) => (n === 1 ? 'request' : 'requests');
      if (!requested) {
        toast({ variant: 'success', message: `${count} draft ${noun(count)} created.` });
      } else if (sendFailed === 0) {
        toast({ variant: 'success', message: `${sent} ${noun(sent)} sent.` });
      } else {
        toast({ variant: 'info', message: `${count} created; ${sent} sent, ${sendFailed} couldn't be sent — send them from the board.` });
      }
      onCreated?.();
      close();
    },
    onError: (err) => toast({ variant: 'error', message: apiError(err, 'Could not create the requests.') }),
  });

  const busy = previewMut.isPending || createMut.isPending;

  function toggle(set: Set<string>, apply: (s: Set<string>) => void, id: string) {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    apply(next);
  }

  // 7/28 C2 — recipient selection derived state.
  const totalRecipients = preview?.recipients.length ?? 0;
  const allRecipientsSelected = totalRecipients > 0 && selectedEmails.size === totalRecipients;
  const toggleAllRecipients = () =>
    setSelectedEmails(allRecipientsSelected ? new Set() : new Set((preview?.recipients ?? []).map((r) => r.email)));

  return (
    <Modal
      open={open}
      onClose={() => { if (!busy) close(); }}
      title={step === 'compose' ? 'New information request' : 'Preview & send'}
      size="lg"
      footer={
        step === 'compose' ? (
          <>
            <Button variant="secondary" onClick={close} disabled={busy}>Cancel</Button>
            <Button
              variant="primary"
              loading={previewMut.isPending}
              disabled={!composeValid || busy}
              onClick={() => previewMut.mutate()}
            >Next: preview</Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setStep('compose')} disabled={busy}>Back</Button>
            <Button variant="secondary" onClick={() => createMut.mutate(false)} disabled={busy}>Create drafts</Button>
            <Button
              variant="primary"
              loading={createMut.isPending}
              disabled={busy || selectedEmails.size === 0}
              onClick={() => createMut.mutate(true)}
            >Send now{selectedEmails.size > 0 ? ` (${selectedEmails.size})` : ''}</Button>
          </>
        )
      }
    >
      {step === 'compose' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          {/* Client */}
          <label style={{ display: 'block' }}>
            <span style={labelStyle}>Client</span>
            {clientLocked ? (
              <div style={lockedStyle}>{clientName ?? defaultClientId} <span style={{ color: 'var(--color-ink-3)' }}>(from context)</span></div>
            ) : (
              <SearchableSelect
                options={(clientsQuery.data ?? []).map(c => ({ value: c.id, label: c.name || c.id }))}
                value={clientId}
                onChange={(v) => { setClientId(v); setPropIds(new Set()); }}
                placeholder={clientsQuery.isLoading ? 'Loading clients…' : 'Search clients…'}
                aria-label="Client"
              />
            )}
          </label>

          {/* Properties */}
          <div>
            <span style={labelStyle}>Properties</span>
            {!clientId ? (
              <p style={hintStyle}>Select a client first.</p>
            ) : propertiesQuery.isLoading ? (
              <p style={hintStyle}>Loading properties…</p>
            ) : activeProps.length === 0 ? (
              <p style={{ ...hintStyle, color: 'var(--color-status-red)' }}>This client has no active properties to request against.</p>
            ) : (
              <>
                {/* 7/28 C3: nothing selected by default. "All active properties" selects them
                    all into the list (below); the list is always visible + scrolls for 20+. */}
                <Checkbox
                  label={`All active properties (${activeProps.length})`}
                  checked={allActiveSelected}
                  onChange={(e) => setPropIds((e.target as HTMLInputElement).checked ? new Set(activeProps.map(p => p.id)) : new Set())}
                />
                <div style={checkListStyle}>
                  {activeProps.map(p => (
                    <Checkbox
                      key={p.id}
                      label={p.name || p.id}
                      checked={propIds.has(p.id)}
                      onChange={() => toggle(propIds, setPropIds, p.id)}
                    />
                  ))}
                </div>
                <p style={hintStyle}>{targetPropertyIds.length} propert{targetPropertyIds.length === 1 ? 'y' : 'ies'} selected · one request each.</p>
              </>
            )}
          </div>

          {/* Document types */}
          <div>
            <span style={labelStyle}>Documents to request</span>
            {docTypesQuery.isLoading ? (
              <p style={hintStyle}>Loading document types…</p>
            ) : docTypes.length === 0 ? (
              <p style={hintStyle}>No document types configured.</p>
            ) : (
              <div style={checkListStyle}>
                {docTypes.map(t => (
                  <Checkbox key={t} label={t} checked={types.has(t)} onChange={() => toggle(types, setTypes, t)} />
                ))}
              </div>
            )}
          </div>

          {/* Optional details */}
          <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
            <label style={{ flex: '1 1 200px' }}>
              <span style={labelStyle}>Title <span style={optStyle}>(optional)</span></span>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Document request" />
            </label>
            <label style={{ flex: '0 0 120px' }}>
              <span style={labelStyle}>Tax year <span style={optStyle}>(opt.)</span></span>
              <Input type="number" value={taxYear} onChange={e => setTaxYear(e.target.value)} placeholder="current" />
            </label>
            <label style={{ flex: '0 0 160px' }}>
              <span style={labelStyle}>Due date <span style={optStyle}>(opt.)</span></span>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </label>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          <div style={summaryStyle}>
            Creating <strong>{targetPropertyIds.length}</strong> request{targetPropertyIds.length === 1 ? '' : 's'}
            {clientName ? <> for <strong>{clientName}</strong></> : null} — one per property,
            each requesting <strong>{chosenTypes.length}</strong> document{chosenTypes.length === 1 ? '' : 's'}.
          </div>

          <div>
            <span style={labelStyle}>Email subject</span>
            <div style={fieldBoxStyle}>{preview?.subject}</div>
          </div>
          <div>
            <span style={labelStyle}>Email body <span style={optStyle}>(edit it so it reads as your own note; the requested items list stays in place)</span></span>
            {preview?.body_html != null ? (
              <RichTextField value={bodyHtml} onChange={setBodyHtml} />
            ) : (
              <pre style={{ ...fieldBoxStyle, whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>{preview?.body_text}</pre>
            )}
          </div>
          <div>
            <span style={labelStyle}>Attachments</span>
            <div style={{ display: 'flex', gap: 'var(--sp-4)', flexWrap: 'wrap' }}>
              <Checkbox label="Active property list (PDF)" checked={attachList} onChange={(e) => setAttachList(e.target.checked)} />
              <Checkbox label="Summary report (PDF)" checked={attachSummary} onChange={(e) => setAttachSummary(e.target.checked)} />
            </div>
            {preview?.attachments && preview.attachments.length > 0 && (
              <p style={hintStyle}>Will send: {preview.attachments.map((a) => a.filename).join(', ')}. Change a box and reopen the preview to refresh the list.</p>
            )}
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-2)' }}>
              <span style={labelStyle}>Recipients ({selectedEmails.size}/{totalRecipients} selected)</span>
              {totalRecipients > 0 && (
                <button
                  type="button"
                  onClick={toggleAllRecipients}
                  style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'var(--brand-primary)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' }}
                >{allRecipientsSelected ? 'Clear all' : 'Select all'}</button>
              )}
            </div>
            {totalRecipients === 0 ? (
              <p style={{ ...hintStyle, color: 'var(--color-status-red)' }}>
                No contact has an email — add one before sending. You can still create drafts.
              </p>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', maxHeight: 220, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', padding: 'var(--sp-2)' }}>
                  {preview?.recipients.map(r => (
                    <label key={r.email} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', cursor: 'pointer', fontSize: 'var(--fs-sm)', color: 'var(--color-ink)' }}>
                      <input type="checkbox" checked={selectedEmails.has(r.email)} onChange={() => toggle(selectedEmails, setSelectedEmails, r.email)} />
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.name}{r.role ? ` · ${r.role}` : ''} &lt;{r.email}&gt;
                      </span>
                      {r.level && <span style={chipStyle}>{LEVEL_LABEL[r.level]}</span>}
                    </label>
                  ))}
                </div>
                {selectedEmails.size === 0 && (
                  <p style={{ ...hintStyle, color: 'var(--color-status-red)', margin: 'var(--sp-1) 0 0' }}>
                    None selected — pick at least one to send, or use Create drafts.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function normalizeValues(data: string[] | { values?: string[] } | undefined): string[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.values || [];
}
function apiError(err: unknown, fallback: string): string {
  return err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
    ? String((err.body as { error: unknown }).error)
    : fallback;
}

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)',
  fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)',
};
const optStyle: React.CSSProperties = { fontWeight: 'var(--fw-regular)', color: 'var(--color-ink-3)' };
const hintStyle: React.CSSProperties = { margin: 'var(--sp-1) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' };
const lockedStyle: React.CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)', background: 'var(--color-bg)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--r-md)', fontSize: 'var(--fs-md)', color: 'var(--color-ink)',
};
const checkListStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', marginTop: 'var(--sp-2)',
  maxHeight: 200, overflowY: 'auto', padding: 'var(--sp-2)',
  border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', background: 'var(--color-bg)',
};
const summaryStyle: React.CSSProperties = {
  padding: 'var(--sp-3)', background: 'var(--brand-primary-bg)', borderRadius: 'var(--r-md)',
  fontSize: 'var(--fs-md)', color: 'var(--color-ink)',
};
const fieldBoxStyle: React.CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)', background: 'var(--color-bg)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink)',
};
const chipStyle: React.CSSProperties = {
  padding: 'var(--sp-1) var(--sp-2)', background: 'var(--color-card)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--r-full)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-2)',
};
